import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTransactions,
  refundTransaction,
  exportTransactionsCsv,
} from '../../services/adminService';
import { parseBackendDate } from '../../utils/parseBackendDate';
import './AdminTransactionsPage.css';

/* ── Types ──────────────────────────────────────────────── */
interface Transaction {
  id: string;
  createdAt: string;
  userEmail: string;
  amount: number;
  currency: string;
  status: 'success' | 'pending' | 'failed' | 'refunded';
  type: 'purchase' | 'subscription' | 'refund';
  provider: 'stripe' | 'apple' | 'google';
  transactionId: string;
}

interface TransactionsResponse {
  data: Transaction[];
  total: number;
  page: number;
  limit: number;
}

interface SummaryStats {
  totalRevenue: number;
  transactionCount: number;
  refundTotal: number;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  TWD: 'NT$',
  CNY: '\u00A5',
  JPY: '\u00A5',
  KRW: '\u20A9',
  THB: '\u0E3F',
  VND: '\u20AB',
  IDR: 'Rp',
  MYR: 'RM',
};

const STATUS_OPTIONS = ['all', 'success', 'pending', 'failed', 'refunded'] as const;
const TYPE_OPTIONS = ['all', 'purchase', 'subscription', 'refund'] as const;
const PROVIDER_OPTIONS = ['all', 'stripe', 'apple', 'google'] as const;
const CURRENCY_OPTIONS = ['all', 'USD', 'TWD', 'CNY', 'JPY', 'KRW', 'THB', 'VND', 'IDR', 'MYR'] as const;

/* ── Component ──────────────────────────────────────────── */
const AdminTransactionsPage: React.FC = () => {
  const { t } = useTranslation('admin');

  /* Filters */
  const [status, setStatus] = useState<string>('all');
  const [type, setType] = useState<string>('all');
  const [provider, setProvider] = useState<string>('all');
  const [currency, setCurrency] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  /* Data */
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<SummaryStats>({ totalRevenue: 0, transactionCount: 0, refundTotal: 0 });

  /* Refund modal */
  const [refundTarget, setRefundTarget] = useState<Transaction | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);

  /* Clipboard toast */
  const [copiedId, setCopiedId] = useState<string | null>(null);

  /* Debounce search */
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  /* Build params */
  const buildParams = useCallback(() => {
    const params: Record<string, unknown> = { page, limit };
    if (status !== 'all') params.status = status;
    if (type !== 'all') params.type = type;
    if (provider !== 'all') params.provider = provider;
    if (currency !== 'all') params.currency = currency;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (debouncedSearch) params.email = debouncedSearch;
    return params;
  }, [page, limit, status, type, provider, currency, dateFrom, dateTo, debouncedSearch]);

  /* Fetch */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res: TransactionsResponse = await getTransactions(buildParams());
      setTransactions(res.data);
      setTotal(res.total);

      /* Compute summary from full result set */
      const successTxns = res.data.filter((tx: Transaction) => tx.status === 'success');
      const refundTxns = res.data.filter((tx: Transaction) => tx.status === 'refunded');
      setSummary({
        totalRevenue: successTxns.reduce((s: number, tx: Transaction) => s + tx.amount, 0),
        transactionCount: res.total,
        refundTotal: refundTxns.reduce((s: number, tx: Transaction) => s + tx.amount, 0),
      });
    } catch {
      setTransactions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* Reset page when filters change */
  useEffect(() => {
    setPage(1);
  }, [status, type, provider, currency, dateFrom, dateTo, debouncedSearch]);

  /* Export CSV */
  const handleExport = async () => {
    try {
      const blob = await exportTransactionsCsv(buildParams());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      /* silent */
    }
  };

  /* Refund */
  const handleRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      await refundTransaction(refundTarget.id, {
        amount: refundAmount ? parseFloat(refundAmount) : undefined,
        reason: refundReason,
      });
      setRefundTarget(null);
      setRefundAmount('');
      setRefundReason('');
      fetchData();
    } catch {
      /* silent */
    } finally {
      setRefunding(false);
    }
  };

  /* Copy transaction ID */
  const copyTxId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  /* Helpers */
  const formatDate = (iso: string) => {
    const d = parseBackendDate(iso);
    if (!d) return '-';
    return d.toLocaleDateString('en-CA') + ' ' + d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
  };

  const formatAmount = (amount: number, cur: string) =>
    `${CURRENCY_SYMBOLS[cur] || ''}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const totalPages = Math.ceil(total / limit);

  /* ── Render ───────────────────────────────────────────── */
  return (
    <div className="admin-txn-page">
      <h1 className="admin-txn-title">{t('pages.transactions', 'Transactions')}</h1>

      {/* Summary stats */}
      <div className="admin-txn-summary">
        <div className="admin-txn-stat-card">
          <span className="admin-txn-stat-value admin-txn-stat-revenue">
            ${summary.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
          <span className="admin-txn-stat-label">{t('transactions.totalRevenue', 'Total Revenue')}</span>
        </div>
        <div className="admin-txn-stat-card">
          <span className="admin-txn-stat-value">{summary.transactionCount.toLocaleString()}</span>
          <span className="admin-txn-stat-label">{t('transactions.count', 'Transactions')}</span>
        </div>
        <div className="admin-txn-stat-card">
          <span className="admin-txn-stat-value admin-txn-stat-refund">
            ${summary.refundTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
          <span className="admin-txn-stat-label">{t('transactions.refundTotal', 'Refund Total')}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-txn-filters">
        <select value={status} onChange={e => setStatus(e.target.value)} className="admin-txn-select">
          {STATUS_OPTIONS.map(o => (
            <option key={o} value={o}>{o === 'all' ? t('transactions.allStatuses', 'All Statuses') : o.charAt(0).toUpperCase() + o.slice(1)}</option>
          ))}
        </select>

        <select value={type} onChange={e => setType(e.target.value)} className="admin-txn-select">
          {TYPE_OPTIONS.map(o => (
            <option key={o} value={o}>{o === 'all' ? t('transactions.allTypes', 'All Types') : o.charAt(0).toUpperCase() + o.slice(1)}</option>
          ))}
        </select>

        <select value={provider} onChange={e => setProvider(e.target.value)} className="admin-txn-select">
          {PROVIDER_OPTIONS.map(o => (
            <option key={o} value={o}>{o === 'all' ? t('transactions.allProviders', 'All Providers') : o.charAt(0).toUpperCase() + o.slice(1)}</option>
          ))}
        </select>

        <select value={currency} onChange={e => setCurrency(e.target.value)} className="admin-txn-select">
          {CURRENCY_OPTIONS.map(o => (
            <option key={o} value={o}>{o === 'all' ? t('transactions.allCurrencies', 'All Currencies') : o}</option>
          ))}
        </select>

        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="admin-txn-date" placeholder="From" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="admin-txn-date" placeholder="To" />

        <div className="admin-txn-search-wrap">
          <svg className="admin-txn-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="admin-txn-search"
            placeholder={t('transactions.searchEmail', 'Search by email...')}
          />
        </div>
      </div>

      {/* Action buttons */}
      <div className="admin-txn-actions">
        <button className="admin-txn-btn admin-txn-btn-secondary" onClick={handleExport}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {t('transactions.exportCsv', 'Export CSV')}
        </button>
        <button className="admin-txn-btn admin-txn-btn-secondary" onClick={fetchData} disabled={loading}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
          </svg>
          {t('transactions.refresh', 'Refresh')}
        </button>
      </div>

      {/* Table */}
      <div className="admin-txn-table-wrap">
        {loading ? (
          <div className="admin-txn-loading">
            <div className="admin-txn-spinner" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="admin-txn-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
              <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>{t('transactions.noResults', 'No transactions found.')}</p>
          </div>
        ) : (
          <table className="admin-txn-table">
            <thead>
              <tr>
                <th>{t('transactions.date', 'Date')}</th>
                <th>{t('transactions.user', 'User')}</th>
                <th>{t('transactions.amount', 'Amount')}</th>
                <th>{t('transactions.status', 'Status')}</th>
                <th>{t('transactions.type', 'Type')}</th>
                <th>{t('transactions.provider', 'Provider')}</th>
                <th>{t('transactions.txId', 'Transaction ID')}</th>
                <th>{t('transactions.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => (
                <tr key={tx.id}>
                  <td className="admin-txn-cell-date">{formatDate(tx.createdAt)}</td>
                  <td className="admin-txn-cell-email">{tx.userEmail}</td>
                  <td className="admin-txn-cell-amount">{formatAmount(tx.amount, tx.currency)}</td>
                  <td>
                    <span className={`admin-txn-badge admin-txn-badge-${tx.status}`}>
                      {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                    </span>
                  </td>
                  <td>{tx.type.charAt(0).toUpperCase() + tx.type.slice(1)}</td>
                  <td>{tx.provider.charAt(0).toUpperCase() + tx.provider.slice(1)}</td>
                  <td>
                    <button className="admin-txn-copy-btn" onClick={() => copyTxId(tx.transactionId)} title={tx.transactionId}>
                      <span className="admin-txn-txid">{tx.transactionId.slice(0, 12)}...</span>
                      {copiedId === tx.transactionId ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" width="14" height="14">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                          <rect x="9" y="9" width="13" height="13" rx="2" />
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </svg>
                      )}
                    </button>
                  </td>
                  <td>
                    {tx.status === 'success' && (
                      <button className="admin-txn-btn admin-txn-btn-danger" onClick={() => { setRefundTarget(tx); setRefundAmount(''); setRefundReason(''); }}>
                        {t('transactions.refund', 'Refund')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-txn-pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="admin-txn-page-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="admin-txn-page-info">
            {t('transactions.pageInfo', 'Page {{page}} of {{total}}', { page, total: totalPages })}
          </span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="admin-txn-page-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}

      {/* Refund modal */}
      {refundTarget && (
        <div className="admin-txn-modal-overlay" onClick={() => setRefundTarget(null)}>
          <div className="admin-txn-modal" onClick={e => e.stopPropagation()}>
            <h2 className="admin-txn-modal-title">{t('transactions.refundTitle', 'Refund Transaction')}</h2>
            <p className="admin-txn-modal-desc">
              {t('transactions.refundDesc', 'Transaction {{id}} for {{amount}}', {
                id: refundTarget.transactionId.slice(0, 12) + '...',
                amount: formatAmount(refundTarget.amount, refundTarget.currency),
              })}
            </p>

            <label className="admin-txn-modal-label">
              {t('transactions.refundAmount', 'Amount (leave empty for full refund)')}
              <input
                type="number"
                step="0.01"
                min="0"
                max={refundTarget.amount}
                value={refundAmount}
                onChange={e => setRefundAmount(e.target.value)}
                className="admin-txn-modal-input"
                placeholder={refundTarget.amount.toString()}
              />
            </label>

            <label className="admin-txn-modal-label">
              {t('transactions.refundReason', 'Reason')}
              <textarea
                value={refundReason}
                onChange={e => setRefundReason(e.target.value)}
                className="admin-txn-modal-textarea"
                rows={3}
                placeholder={t('transactions.refundReasonPlaceholder', 'Describe reason for refund...')}
              />
            </label>

            <div className="admin-txn-modal-actions">
              <button className="admin-txn-btn admin-txn-btn-secondary" onClick={() => setRefundTarget(null)}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="admin-txn-btn admin-txn-btn-danger"
                onClick={handleRefund}
                disabled={refunding || refundReason.trim().length < 3}
              >
                {refunding ? t('transactions.processing', 'Processing...') : t('transactions.confirmRefund', 'Confirm Refund')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransactionsPage;
